import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";

import {
  LOAD_EMPLOYEES,
  SG_GET_DEBRIEF_SCHEDULES,
} from "../../constants/actions";

import {
  GET_ORGANIZATION_GROUP as API_GET_ORGANIZATION_GROUP,
  FETCH_ORGANIZATION_GROUP as API_FETCH_ORGANIZATION_GROUP,
} from "../../constants/api";

import {
  buildComplianceThresholdMatches,
} from "./utils/participationThresholdUtils";

const delay = (ms) => new Promise((r) => setTimeout(r, ms));
const GROUP_DETAIL_THROTTLE_MS = 1000;

const ManagerGroupParticipationScanner = ({ onBack }) => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();

  const {
    auth,
    employees,
    debriefSchedules,
    reportsPending,
  } = useSelector(
    (state) => ({
      auth: state.auth,
      employees: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
      debriefSchedules: Array.isArray(state.debrief_schedule?.debrief_schedule?.results)
        ? state.debrief_schedule.debrief_schedule.results
        : [],
      reportsPending: !!state.debrief_schedule?.reportsPending,
    }),
    shallowEqual
  );

  const orgId = auth?.organization_id || auth?.selectedOrganization?.id || null;

  const [threshold, setThreshold] = useState(5);
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [running, setRunning] = useState(false);
  const [scanProgress, setScanProgress] = useState({
    running: false,
    current: 0,
    total: 0,
    currentGroupId: null,
  });

  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!employees.length) dispatch({ type: LOAD_EMPLOYEES });

    if (orgId) {
      // Load debrief schedules (used for dropdown) only if not already present
      if (!debriefSchedules.length) {
        dispatch({
          type: SG_GET_DEBRIEF_SCHEDULES,
          payload: `type_of=3&organization=${orgId}&page=1&page_size=5000`,
        });
      }
    }
  }, [
    dispatch,
    orgId,
    employees.length,
    debriefSchedules.length,
  ]);

  const fetchPermissionGroupsForScan = async () => {
    if (!orgId) return [];

    cancelledRef.current = false;
    setLoadingGroups(true);
    setScanProgress({ running: true, current: 0, total: 0, currentGroupId: null });

    try {
      const res = await API_GET_ORGANIZATION_GROUP({
        organization: orgId,
        group_type: "permission",
        page: 1,
        page_size: 5000,
      });

      const data = res?.data;
      const list = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      const normalized = [];
      setScanProgress({
        running: true,
        current: 0,
        total: list.length,
        currentGroupId: null,
      });

      for (let i = 0; i < list.length; i += 1) {
        if (cancelledRef.current) return normalized;
        const g = list[i];
        setScanProgress({
          running: true,
          current: i + 1,
          total: list.length,
          currentGroupId: g?.id || null,
        });

        if (Array.isArray(g?.members)) {
          normalized.push(g);
        } else if (g?.id) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const detail = await API_FETCH_ORGANIZATION_GROUP({ id: g.id });
            normalized.push(detail?.data || g);
          } catch (_) {
            normalized.push(g);
          }

          // Throttle detail calls to reduce 429 risk.
          if (i < list.length - 1) {
            // eslint-disable-next-line no-await-in-loop
            await delay(GROUP_DETAIL_THROTTLE_MS);
          }
        } else {
          normalized.push(g);
        }
      }

      return normalized;
    } finally {
      if (!cancelledRef.current) {
        setLoadingGroups(false);
        setScanProgress({ running: false, current: 0, total: 0, currentGroupId: null });
      }
    }
  };

  const selectedSchedule = useMemo(() => {
    const idNum = Number(selectedScheduleId);
    return (
      debriefSchedules.find((s) => Number(s?.id) === idNum) || null
    );
  }, [debriefSchedules, selectedScheduleId]);

  const scheduleComplianceIds = useMemo(() => {
    return Array.isArray(selectedSchedule?.compliance) ? selectedSchedule.compliance : [];
  }, [selectedSchedule?.compliance]);

  const matches = useMemo(() => {
    if (!selectedScheduleId) return [];
    return buildComplianceThresholdMatches({
      permissionGroups,
      employees,
      complianceIds: scheduleComplianceIds,
      threshold,
    });
  }, [permissionGroups, employees, scheduleComplianceIds, threshold, selectedScheduleId]);

  const scheduleLabel = (s) =>
    s?.brief_question ||
    s?.name ||
    s?.title ||
    (s?.date ? new Date(s.date).toLocaleDateString() : null) ||
    `Debrief #${s?.id}`;

  const handleRun = async () => {
    if (!selectedScheduleId) {
      addToast("Select a debrief schedule first", {
        appearance: "info",
        autoDismiss: true,
      });
      return;
    }
    setRunning(true);
    try {
      // Only load groups/members when scanning (avoid unnecessary API load on mount).
      if (!permissionGroups.length) {
        const groups = await fetchPermissionGroupsForScan();
        if (!cancelledRef.current) setPermissionGroups(groups);
      }
      await delay(150);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Wrap>
      <HeaderRow>
        <div>
          <Title>Response Threshold Scanner</Title>
          <Subtitle>
            Find managers whose assigned contacts have fewer than a threshold number
            of responses for a debrief schedule.
          </Subtitle>
        </div>
        <HeaderActions>
          <SecondaryBtn onClick={() => (onBack ? onBack() : null)}>
            Back
          </SecondaryBtn>
        </HeaderActions>
      </HeaderRow>

      <ControlsCard>
        <ControlGrid>
          <Control>
            <Label>Debrief schedule</Label>
            <Select
              value={selectedScheduleId}
              onChange={(e) => setSelectedScheduleId(e.target.value)}
              disabled={!debriefSchedules.length}
            >
              <option value="">Select…</option>
              {debriefSchedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {scheduleLabel(s)}
                </option>
              ))}
            </Select>
            <Hint>
              {selectedSchedule ? `Selected: ${scheduleLabel(selectedSchedule)}` : ""}
            </Hint>
          </Control>

          <Control>
            <Label>Threshold</Label>
            <NumberInput
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
            />
            <Hint>
              Matches are managers where total assigned-contact responses &lt; threshold.
            </Hint>
          </Control>

          <Control>
            <Label>Data status</Label>
            <StatusPill>
              Groups: {loadingGroups ? "Loading…" : `${permissionGroups.length}`}
              {" · "}
              Debriefs: {`${debriefSchedules.length}`}
              {" · "}
              Completed IDs (compliance): {selectedScheduleId ? scheduleComplianceIds.length : "—"}
              {" · "}
              Requests: {selectedScheduleId ? (selectedSchedule?.total_requests ?? "—") : "—"}
            </StatusPill>
            <Hint>
              Compliance IDs represent contacts who completed the selected debrief schedule.
            </Hint>
          </Control>

          <Control>
            <Label>Run</Label>
            <PrimaryBtn
              onClick={handleRun}
              disabled={running || loadingGroups || reportsPending}
            >
              {running ? "Running…" : "Scan"}
            </PrimaryBtn>
          </Control>
        </ControlGrid>
      </ControlsCard>

      <TableCard>
        <TableTitleRow>
          <TableTitle>Matches ({matches.length})</TableTitle>
          <TableMeta>
            Showing managers where assigned contacts have low response counts.
          </TableMeta>
          {scanProgress.running && scanProgress.total > 0 && (
            <TableMeta>
              Fetching group members: {scanProgress.current}/{scanProgress.total}
              {scanProgress.currentGroupId ? ` · group ${scanProgress.currentGroupId}` : ""}
              {" · "}throttle {Math.round(GROUP_DETAIL_THROTTLE_MS / 1000)}s
            </TableMeta>
          )}
        </TableTitleRow>

        {matches.length === 0 ? (
          <EmptyState>
            {selectedScheduleId
              ? "No matches under the current threshold."
              : "Select a debrief schedule to see matches."}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Manager</Th>
                <Th>Email</Th>
                <Th align="right">Assigned contacts</Th>
                <Th align="right">Completed</Th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m) => (
                <tr key={String(m.groupId)}>
                  <Td>{m.managerName}</Td>
                  <Td>{m.managerEmail || "—"}</Td>
                  <Td align="right">{m.memberCount}</Td>
                  <Td align="right">{m.completedCount}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>
    </Wrap>
  );
};

export default ManagerGroupParticipationScanner;

const Wrap = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

const Title = styled.h1`
  margin: 0;
  font-family: "Poppins";
  font-size: 22px;
  font-weight: 600;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 6px 0 0 0;
  font-family: "Poppins";
  font-size: 13px;
  color: #6b7280;
`;

const ControlsCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 14px;
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.6fr 0.7fr;
  gap: 12px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Control = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 600;
  color: #374151;
`;

const Hint = styled.div`
  font-family: "Poppins";
  font-size: 11px;
  color: #6b7280;
  min-height: 14px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-family: "Poppins";
  font-size: 13px;
`;

const NumberInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-family: "Poppins";
  font-size: 13px;
`;

const StatusPill = styled.div`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-family: "Poppins";
  font-size: 12px;
  color: #374151;
  background: #fafafa;
`;

const PrimaryBtn = styled.button`
  padding: 10px 12px;
  background: #476dfa;
  border: none;
  border-radius: 10px;
  color: white;
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

const SecondaryBtn = styled.button`
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #111827;
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const TableTitleRow = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const TableTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  color: #111827;
`;

const TableMeta = styled.div`
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const EmptyState = styled.div`
  padding: 18px 14px;
  font-family: "Poppins";
  font-size: 13px;
  color: #6b7280;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 10px 12px;
  border-bottom: 1px solid #f3f4f6;
  font-family: "Poppins";
  font-size: 13px;
  color: #111827;
`;


